import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { LayoutService } from '../../../shared/services/layout.service';

/**
 * Orbi AI chat panel shell (PCSA-13158).
 *
 * Off-canvas surface that hosts the chat experience. Its width follows the
 * responsive breakpoint spec (PCSA-13312), NOT user resizing:
 *  - < 768px (XS/SM): FULL-SCREEN overlay.
 *  - >= 768px (MD and up): a fixed right-side panel at 35% of the viewport,
 *    from beneath the portal header to the viewport bottom. The page behind
 *    stays visible + interactive (no page-locking backdrop / no reflow).
 * Drag-to-resize was intentionally removed as out of scope (PCSA-13312); the
 * width is now purely breakpoint-driven CSS.
 *
 * This story delivers the SHELL only — header chrome (new / history / minimize),
 * a content slot (`<ng-content>`) for the initial state (13159) and thread
 * (13160), and the footer disclaimer. Responsive detection uses
 * `window.matchMedia` (repo idiom; no @angular/cdk).
 */
@Component({
  selector: 'patient-ui-orbi-panel',
  templateUrl: './orbi-panel.component.html',
  styleUrls: ['./orbi-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrbiPanelComponent
  implements OnInit, OnChanges, AfterViewChecked, OnDestroy
{
  /** Whether the panel is open (driven by LayoutService via the host). */
  @Input() open = false;

  /** Whether the history view is currently showing (highlights the icon). */
  @Input() historyActive = false;

  /**
   * Whether the AI Agent is locked for the session by a locking guardrail
   * (PCSA-13170). When locked, the new-conversation and history header actions
   * are struck out + disabled and do not emit.
   */
  @Input() locked = false;

  /** Request a brand-new conversation (the `+` header action). */
  @Output() newConversation = new EventEmitter<void>();
  /** Toggle the conversation-history view (the history header action). */
  @Output() history = new EventEmitter<void>();
  /** Close / minimize the panel. */
  @Output() closed = new EventEmitter<void>();

  /**
   * Side-panel breakpoint: MD = 768px (Bootstrap `$grid-breakpoints`). At/above
   * this the panel is a fixed 35% right panel; below it is full-screen
   * (PCSA-13312 breakpoint spec).
   */
  private static readonly SIDE_PANEL_QUERY = '(min-width: 768px)';
  private static readonly RESIZE_DEBOUNCE_MS = 150;

  /** The page footer, whose visible height the panel is inset by (see below). */
  private static readonly FOOTER_SELECTOR = 'footer';

  /**
   * Clicks matching these are NOT "outside the canvas" (see `onDocumentClick`):
   *  - `patient-ui-orbi-entry-button` — the bar that OPENS the panel. Its click
   *    would otherwise close the panel in the same tick it opened it.
   *  - `ngb-tooltip-window` / `.tooltip` — the locked-session tooltip, which
   *    ng-bootstrap renders into `body` (`container="body"`), so it is visually
   *    part of the panel but not a DOM descendant of it.
   *  - `ngb-modal-window` / `.modal` — app modals (email verification, session
   *    timeout, IE notice) open on top of everything; a click inside one is an
   *    interaction with that dialog, not a dismissal of the canvas behind it.
   *  - `.ng-dropdown-panel` — the Make an Appointment card's service dropdown,
   *    which passes `appendTo="body"` so its options are not clipped by the
   *    scrolling feed. Same situation as the tooltip above: visually part of the
   *    panel, not a DOM descendant of it. Without this, PICKING A SERVICE would
   *    close the whole canvas.
   */
  private static readonly OUTSIDE_CLICK_EXEMPT = [
    'patient-ui-orbi-entry-button',
    'ngb-tooltip-window',
    '.tooltip',
    'ngb-modal-window',
    '.modal',
    '.ng-dropdown-panel',
  ].join(',');

  /** True when the viewport is wide enough for the 35% side panel (>=768px). */
  isDesktop = true;

  /**
   * How far the panel's bottom edge is raised, in px, so it TUCKS UNDER the page
   * footer instead of covering it (design review 08/06: the canvas "must span
   * from the header navigation to the footer" and "cannot cover footer links").
   *
   * This cannot be a static CSS value. The footer sits at the end of the page
   * flow, so how much of it is on screen depends entirely on scroll position:
   * mid-page there is none and the panel should reach the viewport bottom, but
   * scrolled to the end the footer occupies a band whose height varies with the
   * viewport (the footer's links reflow into different numbers of rows). So the
   * inset is measured from the live footer rect and kept in sync with scroll +
   * resize. Bound to `bottom` on the desktop panel only — below MD the panel is
   * a full-screen overlay above everything, where there is no page visible to
   * tuck under.
   */
  footerInset = 0;

  /**
   * Header title. The panel is a single surface that swaps its BODY between the
   * conversation and the history list, so the title has to name whichever view
   * is actually showing — a list of past conversations under an "Ask AI" title
   * gave no indication of where you were, and no way to tell you had left the
   * chat (design review 08/06).
   */
  get title(): string {
    return this.historyActive ? 'Conversation History' : 'Ask AI';
  }

  @ViewChild('panelHeading')
  private panelHeading?: ElementRef<HTMLElement>;

  /** True while the feedback bottom sheet is open on top of the panel. */
  private feedbackSheetOpen = false;

  private pendingHeadingFocus = false;

  private viewportDebounce?: ReturnType<typeof setTimeout>;

  /** The in-flight scroll re-measure frame, if any (see `onWindowScroll`). */
  private scrollFrame?: number;

  /** Watches the page's height for footer moves without a scroll (see `watchPageScroll`). */
  private pageResizeObserver?: ResizeObserver;
  private readonly destroyed = new Subject<void>();

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly layoutService: LayoutService,
    private readonly hostRef: ElementRef<HTMLElement>,
    private readonly zone: NgZone
  ) {}

  ngOnInit(): void {
    this.isDesktop = window.matchMedia(
      OrbiPanelComponent.SIDE_PANEL_QUERY
    ).matches;
    this.measureFooterInset();
    this.watchPageScroll();
    // Track whether the feedback sheet is open so Escape can dismiss the topmost
    // layer (the sheet) before the panel itself (PR #3136 review).
    this.layoutService
      .getOrbiFeedbackSheet()
      .pipe(takeUntil(this.destroyed))
      .subscribe((request) => {
        this.feedbackSheetOpen = request !== null;
      });
  }

  /**
   * Re-measure the footer tuck each time the panel OPENS. The component instance
   * outlives any single open (only the inner `*ngIf` content is destroyed), so a
   * measurement taken once in `ngOnInit` would be stale: the user typically
   * scrolls before opening the panel, which is exactly what changes how much
   * footer is on screen.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open']?.currentValue) {
      this.measureFooterInset();
      this.pendingHeadingFocus = true;
    }
  }

  ngAfterViewChecked(): void {
    if (!this.pendingHeadingFocus || !this.panelHeading) {
      return;
    }
    this.pendingHeadingFocus = false;
    this.panelHeading.nativeElement.focus();
  }

  ngOnDestroy(): void {
    if (this.viewportDebounce) {
      clearTimeout(this.viewportDebounce);
    }
    // The scroll listener is registered manually (see `watchPageScroll`), so
    // unlike an @HostListener it is not torn down for us. Cancel any frame still
    // pending too: it closes over `cdr` and would otherwise fire after destroy.
    window.removeEventListener('scroll', this.onPageScroll);
    this.pageResizeObserver?.disconnect();
    if (this.scrollFrame !== undefined) {
      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = undefined;
    }
    this.destroyed.next();
    this.destroyed.complete();
  }

  onNewConversation(): void {
    // No-op while locked (PCSA-13170): the button is also disabled/struck out.
    if (this.locked) {
      return;
    }
    this.newConversation.emit();
  }

  onHistory(): void {
    // Openable even while locked (PCSA-13170, mobile parity): the history list
    // grays out + disables the other conversations, so browsing it cannot bypass
    // the lock. Only NEW conversation (above) is blocked while locked.
    this.history.emit();
  }

  onClose(): void {
    this.closed.emit();
  }

  /**
   * Escape dismisses the TOPMOST layer first (PR #3136 review): if the feedback
   * sheet is open, close only the sheet; a second Escape then closes the panel.
   */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (!this.open) {
      return;
    }
    if (this.feedbackSheetOpen) {
      this.layoutService.closeOrbiFeedbackSheet();
      return;
    }
    this.onClose();
    this.layoutService.requestOrbiEntryFocus();
  }

  /**
   * Re-evaluate the side-panel vs full-screen breakpoint on viewport resize
   * (debounced). Width itself is CSS-driven (45vw / 35vw / 100%); this only flips the
   * `isDesktop` class that selects between the two layouts.
   */
  @HostListener('window:resize')
  onViewportResize(): void {
    if (this.viewportDebounce) {
      clearTimeout(this.viewportDebounce);
    }
    this.viewportDebounce = setTimeout(() => {
      this.isDesktop = window.matchMedia(
        OrbiPanelComponent.SIDE_PANEL_QUERY
      ).matches;
      this.measureFooterInset();
      this.cdr.markForCheck();
    }, OrbiPanelComponent.RESIZE_DEBOUNCE_MS);
  }

  /**
   * Subscribe to page scroll OUTSIDE Angular, passively.
   *
   * This used to be an `@HostListener('window:scroll')`, which runs inside the
   * zone: every scroll event then triggers a full change-detection pass over the
   * whole application, not just this panel, for the entire duration of a scroll
   * gesture (PR 3170 review). Registering it here instead keeps the measurement
   * but takes the app-wide CD out of the hot path — `onWindowScroll` re-enters
   * the zone once per frame, and only when the value actually changed.
   *
   * Same treatment `ui-select` gives its own scroll listener (`select.component`
   * → `watchScrollAncestors`): "a scroll listener must not schedule change
   * detection on every frame."
   */
  private watchPageScroll(): void {
    this.zone.runOutsideAngular(() => {
      window.addEventListener('scroll', this.onPageScroll, { passive: true });
      // The footer also moves when the page's own content changes height with no
      // scroll at all: after a refresh the route renders short, the footer sits
      // high, and then the page's data lands and pushes it below the fold. A
      // panel opened in that window measured the tall footer band and kept its
      // bottom edge raised over empty page. Re-measure on body resize too, on the
      // same one-frame path as scroll.
      if (typeof ResizeObserver !== 'undefined') {
        const body = this.hostRef.nativeElement.ownerDocument?.body;
        if (body) {
          this.pageResizeObserver = new ResizeObserver(this.onPageScroll);
          this.pageResizeObserver.observe(body);
        }
      }
    });
  }

  /** Arrow property so add/removeEventListener see one stable reference. */
  private readonly onPageScroll = (): void => this.onWindowScroll();

  /**
   * Keep the footer tuck in sync while the page scrolls behind the panel. The
   * footer enters and leaves the viewport as the user scrolls, so the inset has
   * to be re-measured — a value computed once on open is wrong the moment the
   * page moves.
   *
   * Coalesced onto the next animation frame rather than DEBOUNCED like
   * `onViewportResize`. A trailing debounce would be wrong here, not merely
   * conservative: the inset would settle only after the scroll stopped, so the
   * panel's bottom edge would lag the footer and paint over the very links the
   * tuck exists to keep clear — the exact bug this listener prevents. Resize can
   * afford to wait because nothing moves between the drag and the settle; scroll
   * cannot. A frame is the shortest wait that still bounds the work: scroll
   * events can outpace the compositor, and rAF collapses a burst into ONE
   * layout read + one CD pass per painted frame, which is the most the user can
   * actually see.
   *
   * `measureFooterInset` runs inside the frame (it reads layout, so it belongs
   * with the paint), and the zone is re-entered only when the value moved —
   * scrolling in a region where no footer is visible costs nothing downstream.
   */
  onWindowScroll(): void {
    if (!this.open || !this.isDesktop) {
      return;
    }
    if (this.scrollFrame !== undefined) {
      return;
    }
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = undefined;
      const previous = this.footerInset;
      this.measureFooterInset();
      if (this.footerInset !== previous) {
        this.zone.run(() => this.cdr.markForCheck());
      }
    });
  }

  /**
   * Measure how much of the page footer is currently on screen and raise the
   * panel's bottom edge by exactly that much, so the panel spans header → footer
   * and never paints over the footer's links (design review 08/06).
   *
   * `getBoundingClientRect().top` is the footer's distance from the viewport top:
   * when it is below the fold the overlap is zero (clamped), and as the footer
   * scrolls up the overlap grows to the footer's own height. Clamped at both ends
   * so the panel can neither extend past the viewport bottom nor collapse to
   * nothing on a short page whose footer fills the screen.
   */
  private measureFooterInset(): void {
    const footer = this.hostRef.nativeElement.ownerDocument?.querySelector(
      OrbiPanelComponent.FOOTER_SELECTOR
    );
    if (!footer) {
      this.footerInset = 0;
      return;
    }
    const viewportHeight = window.innerHeight;
    const overlap = viewportHeight - footer.getBoundingClientRect().top;
    this.footerInset = Math.max(0, Math.min(overlap, viewportHeight));
  }

  /**
   * Any interaction OUTSIDE the canvas closes it (design review 08/06 —
   * confirmed expected behavior).
   *
   * Only closes on a click it can POSITIVELY attribute to outside the canvas;
   * anything ambiguous is left alone, because a panel that closes on a click the
   * user considers "inside" is far worse than one that occasionally needs the
   * minimize button. Three cases are therefore not "outside":
   *  - a target inside this host;
   *  - a target matching `OUTSIDE_CLICK_EXEMPT` (the entry bar, body-rendered
   *    tooltips/modals);
   *  - a target no longer connected to the document. That last one is not
   *    hypothetical: resuming a conversation runs `detectChanges()` inside the
   *    click handler (to focus the thread input), which swaps the history list
   *    out and detaches the very element that was clicked — by the time this
   *    bubble-phase listener runs, `host.contains()` reports false for a click
   *    that came from INSIDE the panel, and the panel would close on resume.
   *
   * Bubble phase (Angular's `@HostListener` cannot bind capture), so a page
   * handler calling `stopPropagation` on a click would keep the panel open; that
   * fails safe in the direction above.
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.open) {
      return;
    }
    const target = event.target as Node | null;
    if (!target || !target.isConnected) {
      return;
    }
    if (this.hostRef.nativeElement.contains(target)) {
      return;
    }
    const element =
      target instanceof Element ? target : target.parentElement ?? null;
    if (element?.closest(OrbiPanelComponent.OUTSIDE_CLICK_EXEMPT)) {
      return;
    }
    this.onClose();
  }
}
